import React from 'react';
import { Box, Paper, Typography, Divider, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';

// Import custom components
import AddProductSection from './calculator/products/AddProductSection';
import PriceSummary from './calculator/PriceSummary';
// DEMO: Use new BusinessPhoneDemo instead of BusinessPhone
import BusinessPhoneDemo from './calculator/products/BusinessPhone/BusinessPhoneDemo';
import CallTracking from './calculator/products/CallTracking/CallTracking';
import AIConversation from './calculator/products/AIConversation/AIConversation';
import Callback from './calculator/products/Callback/Callback';
import PhoneNumbers from './calculator/products/PhoneNumbers';
import VoiceTraffic from './calculator/products/VoiceTraffic';
import OmnichannelChat from './calculator/products/OmnichannelChat/OmnichannelChat';
import EniqAI from './calculator/products/EniqAI';
import CallTrackingSettingsModal from './calculator/modals/CallTrackingSettingsModal';
import AISettingsModal from './calculator/modals/AISettingsModal';
import SubscriptionSettingsDialog from './calculator/modals/SubscriptionSettingsDialog';
import BusinessPhoneSettingsDialog from './calculator/modals/BusinessPhoneSettingsDialog';

// Import context
import { useCalculator } from '../contexts/CalculatorContext';

/**
 * DEMO Calculator component - showcasing new Package Tier UI
 */
const CostCalculatorDemo = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const {
    // Refs
    summaryRef,
    summaryStyle,

    // Products
    productOptions,
    selectedProductsData,
    isProductSelected,
    toggleProduct,

    // Phone Numbers
    phoneNumbers,
    setPhoneNumbers,
    addPhoneNumber,
    removePhoneNumber,
    updatePhoneNumber,
    phoneNumbersCost,
    phoneNumbersNeeded,
    setPhoneNumbersNeeded,

    // Voice Traffic
    voiceTrafficCost,

    // Callback
    callbackCost,

    // Price Summary
    totalCost,

    // Business Phone
    businessPhoneCost,
    businessPhoneType,

    // Call Tracking
    callTrackingCost,
    callTrackingSettingsOpen,
    hasCustomSettings,
    hasCallTrackingAnyCustomSettings,
    setCallTrackingSettingsOpen,
    callTrackingType,
    callMeButtonEnabled,
    staticSources,
    setStaticSources,
    numberFormatsQuantity,
    handleApplyCallTrackingSettings,

    // AI Conversation Intelligence
    aiConversationIntelligenceCost,
    aiSettingsOpen,
    hasAiCustomSettings,
    setAiSettingsOpen,
    handleApplyAISettings,
    nonEnglishAnalysis,
    handleApplySubscriptionSettings,

    // Business Phone
    businessPhoneSettingsOpen,
    setBusinessPhoneSettingsOpen,

    // Global Settings
    subscriptionSettingsOpen,
    setSubscriptionSettingsOpen,
    subscription90Days,
    subscription180Days,
    subscription12Month,
    subscriptionSplitPayment,
    customDiscount360,
    customDiscount180,
    region,

    // Currency
    currency,

    // Summary
    summaryItems,
    summaryItemsForSubscription,
    total,
    subscriptionPrices,

    // Tab specific ID
    instanceTabId
  } = useCalculator();


  // If instanceTabId is not yet defined, show a loader
  if (!instanceTabId) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Handle opening subscription settings dialog
  const handleOpenSubscriptionSettings = () => {
    setSubscriptionSettingsOpen(true);
  };

  return (
    <Box sx={{ mt: 4 }}>
      {/* DEMO Banner */}
      <Paper
        elevation={0}
        sx={{
          mb: 3,
          p: 2,
          bgcolor: 'rgba(0, 200, 83, 0.1)',
          border: '2px dashed #00C853',
          borderRadius: 2,
          textAlign: 'center'
        }}
      >
        <Typography variant="h6" sx={{ color: '#00C853', fontWeight: 600 }}>
          🚀 DEMO: Package-Based Pricing UI
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Business Phone component оновлено з вибором тарифів Basic / Pro / Corporate
        </Typography>
      </Paper>

      {/* Modals and Dialogs */}
      <CallTrackingSettingsModal
        open={callTrackingSettingsOpen}
        onClose={() => setCallTrackingSettingsOpen(false)}
        onApply={handleApplyCallTrackingSettings}
        callTrackingType={callTrackingType}
        callMeButtonEnabled={callMeButtonEnabled}
        staticSources={staticSources}
        numberFormatsQuantity={numberFormatsQuantity}
        hasCustomSettings={hasCustomSettings}
      />
      <AISettingsModal
        open={aiSettingsOpen}
        onClose={() => setAiSettingsOpen(false)}
        onApply={handleApplyAISettings}
        initialSettings={{
          nonEnglishAnalysis,
          hasAiCustomSettings,
        }}
      />
      <BusinessPhoneSettingsDialog
        open={businessPhoneSettingsOpen}
        onClose={() => setBusinessPhoneSettingsOpen(false)}
      />
      <SubscriptionSettingsDialog
        open={subscriptionSettingsOpen}
        onClose={() => setSubscriptionSettingsOpen(false)}
        onApply={handleApplySubscriptionSettings}
        subscription90Days={subscription90Days}
        subscription180Days={subscription180Days}
        subscription12Month={subscription12Month}
        subscriptionSplitPayment={subscriptionSplitPayment}
        customDiscount360={customDiscount360}
        customDiscount180={customDiscount180}
        region={region}
        monthlyTotal={total}
      />

      {/* Main Layout */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 420px' }, gap: 3.5 }}>
        {/* Products Column */}
        <Box>
          <AddProductSection
            productOptions={productOptions()}
            isProductSelected={isProductSelected}
            toggleProduct={toggleProduct}
          />
          {selectedProductsData.map((product) => {
            // DEMO: Use BusinessPhoneDemo instead of BusinessPhone
            if (product.id === 'business-phone' && product.active) {
              return (
                <Paper key={product.id} elevation={0} sx={{
                  bgcolor: 'white',
                  borderRadius: theme.spacing(1.5),
                  border: '2px solid #00C853', // Highlighted border for demo
                  boxShadow: '0 0 20px rgba(0, 200, 83, 0.15)',
                  mb: 3,
                  p: { xs: 3, md: 4 }
                }}>
                  <BusinessPhoneDemo
                    product={product}
                    toggleProduct={toggleProduct}
                  />
                </Paper>
              );
            }
            // Call Tracking component
            else if (product.id === 'call-tracking' && product.active) {
              return (
                <Paper key={product.id} elevation={0} sx={{
                  bgcolor: 'white',
                  borderRadius: theme.spacing(1),
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: theme.shadows[2],
                  },
                  mb: 3,
                  p: 3
                }}>
                  <CallTracking
                    product={product}
                    toggleProduct={toggleProduct}
                    hasCustomSettings={hasCallTrackingAnyCustomSettings}
                    openSettings={() => setCallTrackingSettingsOpen(true)}
                    callTrackingType={callTrackingType}
                    staticSources={staticSources}
                    setStaticSources={setStaticSources}
                    phoneNumbersNeeded={phoneNumbersNeeded}
                  />
                </Paper>
              );
            }
            // AI Conversation Intelligence component
            else if (product.id === 'ai-conversation' && product.active) {
              return (
                <Paper key={product.id} elevation={0} sx={{
                  bgcolor: 'white',
                  borderRadius: theme.spacing(1),
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: theme.shadows[2],
                  },
                  mb: 3,
                  p: 3
                }}>
                  <AIConversation
                    {...product}
                    price={aiConversationIntelligenceCost}
                    hasCustomSettings={hasAiCustomSettings}
                    onOpenSettings={() => setAiSettingsOpen(true)}
                    currency={currency}
                    toggleProduct={toggleProduct}
                  />
                </Paper>
              );
            }
            // Voice Traffic component
            else if (product.id === 'voice-traffic' && product.active) {
              return (
                <Paper key={product.id} elevation={0} sx={{
                  bgcolor: 'white',
                  borderRadius: theme.spacing(1),
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: theme.shadows[2],
                  },
                  mb: 3,
                  p: 3
                }}>
                  <VoiceTraffic
                    product={product}
                    toggleProduct={toggleProduct}
                  />
                </Paper>
              );
            }
            // Callback component
            else if (product.id === 'callback' && product.active) {
              return (
                <Paper key={product.id} elevation={0} sx={{
                  bgcolor: 'white',
                  borderRadius: theme.spacing(1),
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: theme.shadows[2],
                  },
                  mb: 3,
                  p: 3
                }}>
                  <Callback
                    {...product}
                    price={callbackCost}
                    currency={currency}
                    toggleProduct={toggleProduct}
                  />
                </Paper>
              );
            }
            // Omnichannel Chat component
            else if (product.id === 'omnichannel-chat' && product.active) {
              return (
                <Paper key={product.id} elevation={0} sx={{
                  bgcolor: 'white',
                  borderRadius: theme.spacing(1),
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: theme.shadows[2],
                  },
                  mb: 3,
                  p: 3
                }}>
                  <OmnichannelChat
                    product={product}
                    toggleProduct={toggleProduct}
                  />
                </Paper>
              );
            }
            // Eniq AI component
            else if (product.id === 'eniq-ai' && product.active) {
              return (
                <Paper key={product.id} elevation={0} sx={{
                  bgcolor: 'white',
                  borderRadius: theme.spacing(1),
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  boxShadow: 'none',
                  p: 3,
                  mb: 3
                }}>
                  <EniqAI />
                </Paper>
              );
            }
            // Default case
            return null;
          })}
          {/* Phone Numbers Section */}
          {selectedProductsData.find(p => p.id === 'phone-numbers')?.active && (
            <Paper elevation={0} sx={{
              bgcolor: 'white',
              borderRadius: theme.spacing(1),
              border: '1px solid rgba(0, 0, 0, 0.12)',
              boxShadow: 'none',
              '&:hover': {
                boxShadow: theme.shadows[2],
              },
              mb: 3,
              p: 3
            }}>
              <PhoneNumbers
                id="phone-numbers"
                phoneNumbers={phoneNumbers}
                setPhoneNumbers={setPhoneNumbers}
                currency={currency}
                toggleProduct={toggleProduct}
              />
            </Paper>
          )}
        </Box>

        {/* Summary Column */}
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <PriceSummary
            summaryItems={summaryItems}
            summaryItemsForSubscription={summaryItemsForSubscription}
            total={total}
            currency={currency}
            summaryRef={summaryRef}
            summaryStyle={summaryStyle}
            subscriptionPrices={subscriptionPrices}
            show90DaySubscription={subscription90Days}
            show180DaySubscription={subscription180Days}
            show12MonthContract={subscription12Month}
            showSplitPayment={subscriptionSplitPayment}
            region={region}
            onSubscriptionSettingsClick={handleOpenSubscriptionSettings}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default CostCalculatorDemo;
